"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, BookOpen, Calendar, FileText, ChevronRight, RefreshCw } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
const articlesCopy = {
  loadErrorTitle: {
    en: "Unable to load technical articles",
    zh: "无法加载技术文章",
    es: "No se pudieron cargar los artículos técnicos",
    de: "Fachartikel konnten nicht geladen werden",
    fr: "Impossible de charger les articles techniques",
    pt: "Não foi possível carregar os artigos técnicos"
  },
  loadErrorDesc: {
    en: "Please check your connection or retry loading the resources.",
    zh: "请检查网络连接或重试加载资源。",
    es: "Compruebe su conexión o vuelva a intentar cargar los recursos.",
    de: "Bitte prüfen Sie Ihre Verbindung oder versuchen Sie es erneut.",
    fr: "Veuillez vérifier votre connexion ou réessayer le chargement.",
    pt: "Verifique sua conexão ou tente recarregar os recursos."
  },
  retry: {
    en: "Retry loading",
    zh: "重试加载",
    es: "Reintentar carga",
    de: "Erneut versuchen",
    fr: "Réessayer le chargement",
    pt: "Tentar novamente"
  },
  emptyTitle: {
    en: "No related articles found",
    zh: "未找到相关文章",
    es: "No se encontraron artículos relacionados",
    de: "Keine relevanten Artikel gefunden",
    fr: "Aucun article connexe trouvé",
    pt: "Nenhum artigo relacionado encontrado"
  },
  emptyDesc: {
    en: "Explore our complete catalog of materials guides and engineering specifications.",
    zh: "浏览我们完整的材料指南与工程技术规范目录。",
    es: "Explore nuestro catálogo completo de guías de materiales y especificaciones de ingeniería.",
    de: "Entdecken Sie unseren Gesamtkatalog für Werkstoffleitfäden und technische Daten.",
    fr: "Découvrez notre catalogue complet de guides matières et de spécifications techniques.",
    pt: "Explore nosso catálogo completo de guias de materiais e especificações de engenharia."
  },
  browseResources: {
    en: "Browse technical resources",
    zh: "浏览技术资源",
    es: "Explorar recursos técnicos",
    de: "Fachressourcen durchsuchen",
    fr: "Parcourir les ressources techniques",
    pt: "Explorar recursos técnicos"
  },
  defaultCategory: {
    en: "Technical Guide",
    zh: "技术指南",
    es: "Guía técnica",
    de: "Technischer Leitfaden",
    fr: "Guide technique",
    pt: "Guia técnico"
  },
  defaultExcerpt: {
    en: "Detailed material properties, mesh aperture calculations, and selection parameters for industrial processing.",
    zh: "工业加工过程中的详细材料特性、丝网孔径计算与选型参数。",
    es: "Propiedades detalladas del material, cálculos de abertura de malla y parámetros de selección para procesos industriales.",
    de: "Detaillierte Werkstoffeigenschaften, Maschenweitenberechnungen und Auswahlparameter für industrielle Verfahren.",
    fr: "Propriétés matières détaillées, calculs d'ouverture de maille et paramètres de sélection pour les procédés industriels.",
    pt: "Propriedades detalhadas de materiais, cálculos de abertura de malha e parâmetros de seleção para processos industriais."
  },
  readArticle: {
    en: "Read article",
    zh: "阅读文章",
    es: "Leer artículo",
    de: "Artikel lesen",
    fr: "Lire l'article",
    pt: "Ler artigo"
  },
  prev: {
    en: "Previous",
    zh: "上一页",
    es: "Anterior",
    de: "Zurück",
    fr: "Précédent",
    pt: "Anterior"
  },
  next: {
    en: "Next",
    zh: "下一页",
    es: "Siguiente",
    de: "Weiter",
    fr: "Suivant",
    pt: "Próximo"
  },
  eyebrow: {
    en: "Knowledge Base",
    zh: "知识库",
    es: "Base de conocimientos",
    de: "Wissensdatenbank",
    fr: "Base de connaissances",
    pt: "Base de conhecimento"
  },
  sectionTitle: {
    en: "Related materials and technical insights",
    zh: "相关材料与技术见解",
    es: "Materiales relacionados e información técnica",
    de: "Verwandte Werkstoffe und technische Einblicke",
    fr: "Matériaux associés et éclairages techniques",
    pt: "Materiais relacionados e dados técnicos"
  },
  sectionSubtitle: {
    en: "Compare alloy compositions, corrosion ratings, and wire mesh specifications for industrial filtration.",
    zh: "对比工业过滤应用中的合金成分、耐腐蚀评级与丝网技术规范。",
    es: "Compare composiciones de aleaciones, clasificaciones de corrosión y especificaciones de tela metálica para filtración industrial.",
    de: "Vergleichen Sie Legierungszusammensetzungen, Korrosionsklassen und Gewebespezifikationen für die industrielle Filtration.",
    fr: "Comparez les compositions d'alliages, les indices de corrosion et les spécifications de toiles pour la filtration industrielle.",
    pt: "Compare composições de ligas, classificações de corrosão e especificações de telas metálicas para filtração industrial."
  },
  allMaterials: {
    en: "All materials",
    zh: "所有材料",
    es: "Todos los materiales",
    de: "Alle Werkstoffe",
    fr: "Tous les matériaux",
    pt: "Todos os materiais"
  },
  resourceCenter: {
    en: "Resource center",
    zh: "技术资源中心",
    es: "Centro de recursos",
    de: "Ressourcenzentrum",
    fr: "Centre de ressources",
    pt: "Centro de recursos"
  }
};
function getLocaleTag(lang: Lang): string {
  switch (lang) {
    case "zh":
      return "zh-CN";
    case "es":
      return "es-ES";
    case "de":
      return "de-DE";
    case "fr":
      return "fr-FR";
    case "pt":
      return "pt-PT";
    case "en":
    default:
      return "en-US";
  }
}
function ArticlesConsumer() {
  const {
    posts,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage
  } = usePosts();
  const {
    lang
  } = useLanguage();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({
        length: 6
      }).map((_, index) => <div key={index} data-index={index} className="flex flex-col rounded-lg border border-border bg-card p-6">
            <Skeleton className="mb-4 h-5 w-24 rounded-md" />
            <Skeleton className="mb-2 h-6 w-full rounded-md" />
            <Skeleton className="mb-4 h-4 w-3/4 rounded-md" />
            <Skeleton className="mt-auto h-4 w-1/3 rounded-md" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-card p-12 text-center">
        <FileText className="mb-3 size-10 text-muted-foreground" aria-hidden="true" />
        <p className="text-base font-semibold text-foreground">
          {pick(lang, articlesCopy.loadErrorTitle)}
        </p>
        <p className="mt-1 max-w-md text-sm text-muted-foreground">
          {pick(lang, articlesCopy.loadErrorDesc)}
        </p>
        <Button variant="outline" size="sm" onClick={() => void refetch()} className="mt-4">
          <RefreshCw className="mr-2 size-3.5" aria-hidden="true" />
          <span>{pick(lang, articlesCopy.retry)}</span>
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card p-12 text-center">
        <BookOpen className="mb-3 size-10 text-muted-foreground" aria-hidden="true" />
        <p className="text-base font-medium text-foreground">
          {pick(lang, articlesCopy.emptyTitle)}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {pick(lang, articlesCopy.emptyDesc)}
        </p>
        <Button asChild variant="outline" size="sm" className="mt-4">
          <Link to="/TechnicalResourcesPage">
            <span>{pick(lang, articlesCopy.browseResources)}</span>
            <ArrowRight className="ml-1.5 size-3.5" aria-hidden="true" />
          </Link>
        </Button>
      </div>;
  }
  const locale = getLocaleTag(lang);
  return <div className="space-y-8">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {posts.map((post, index) => {
        const categoryName = post.categories && post.categories.length > 0 ? post.categories[0].name : pick(lang, articlesCopy.defaultCategory);
        const formattedDate = post.date ? new Date(post.date).toLocaleDateString(locale, {
          year: "numeric",
          month: lang === "zh" ? "numeric" : "short",
          day: "numeric"
        }) : null;
        return <Card key={post.id} data-index={index} className="flex h-full flex-col justify-between border-border bg-card rounded-lg transition-colors duration-200 hover:border-primary/40">
              <div>
                <CardHeader className="gap-2.5 pb-3">
                  <div className="flex items-center justify-between gap-2">
                    <Badge variant="outline" className="bg-muted text-foreground font-mono mf-caption border-border">
                      {categoryName}
                    </Badge>
                    {formattedDate && <span className="inline-flex items-center gap-1 mf-caption text-muted-foreground">
                        <SafeHydrate><Calendar className="size-3.5" aria-hidden="true" /></SafeHydrate>
                        <span>{formattedDate}</span>
                      </span>}
                  </div>
                  <CardTitle className="mf-h3 font-semibold leading-snug tracking-tight text-card-foreground">
                    {post.link ? <Link to={post.link} className="transition-colors hover:text-primary">
                        {post.title}
                      </Link> : post.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pb-4">
                  {post.excerpt ? <CardDescription className="line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                      {post.excerpt.replace(/<[^>]*>?/gm, "")}
                    </CardDescription> : <CardDescription className="text-sm text-muted-foreground">
                      {pick(lang, articlesCopy.defaultExcerpt)}
                    </CardDescription>}
                </CardContent>
              </div>

              <CardFooter className="pt-0">
                <Button asChild variant="ghost" size="sm" className="-ml-3 px-3 mf-caption font-semibold text-primary hover:bg-primary/10 hover:text-primary">
                  <Link to={post.link || "/TechnicalResourcesPage"}>
                    <span>{pick(lang, articlesCopy.readArticle)}</span>
                    <ArrowRight className="ml-1 size-3.5" aria-hidden="true" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>;
      })}
      </div>

      {(hasNext || hasPrev) && <div className="flex items-center justify-between border-t border-border pt-6">
          <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrev}>
            {pick(lang, articlesCopy.prev)}
          </Button>
          <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNext}>
            {pick(lang, articlesCopy.next)}
          </Button>
        </div>}
    </div>;
}
export default function RelatedArticles() {
  const {
    lang
  } = useLanguage();
  return <section id="relatedarticles" data-nav="light" className="bg-background mf-section text-foreground">
      <div className="mf-container">
        <div className="mb-10 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end lg:mb-12">
          <div className="max-w-2xl space-y-2">
            <Badge variant="outline" className="mf-caption font-semibold uppercase tracking-wider text-muted-foreground">
              {pick(lang, articlesCopy.eyebrow)}
            </Badge>
            <h2 className="mf-h2 font-bold tracking-tight text-foreground">
              {pick(lang, articlesCopy.sectionTitle)}
            </h2>
            <p className="mf-caption text-muted-foreground sm:text-base">
              {pick(lang, articlesCopy.sectionSubtitle)}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Button asChild variant="outline" size="sm">
              <Link to="/MaterialsAndSurfaceFinishesPage">
                <span>{pick(lang, articlesCopy.allMaterials)}</span>
                <ChevronRight className="ml-1 size-3.5" aria-hidden="true" />
              </Link>
            </Button>
            <Button asChild variant="default" size="sm">
              <Link to="/TechnicalResourcesPage">
                <span>{pick(lang, articlesCopy.resourceCenter)}</span>
                <ArrowRight className="ml-1 size-3.5" aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </div>

        <Separator className="mb-10" />

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 6,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <ArticlesConsumer />
        </PostsProvider>
      </div>
    </section>;
}